import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { StarRating } from './Reviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public verified-review form.
 * Shown after user confirms email matches someone who started this tour.
 * Flow:
 *   1. User enters email → check eligibility.
 *   2. If eligible + no existing review: show rating + first_name + comment.
 *   3. Submit via /api/reviews/verified.
 */
const VerifiedReviewForm = ({ tourId, prefillEmail = '', prefillFirstName = '', onSubmitted }) => {
  const [step, setStep] = useState(prefillEmail ? 'checking' : 'email');
  const [email, setEmail] = useState(prefillEmail);
  const [firstName, setFirstName] = useState(prefillFirstName);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ineligibleReason, setIneligibleReason] = useState('');

  const checkEligibility = async (emailToCheck) => {
    const e = (emailToCheck || '').trim().toLowerCase();
    if (!e) {
      toast.error('Please enter your email');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/reviews/eligibility`, {
        params: { email: e, tour_id: tourId }
      });
      if (!data.eligible) {
        setIneligibleReason(data.reason || 'You must start this tour before leaving a review.');
        setStep('ineligible');
        return;
      }
      if (data.has_review) {
        setIneligibleReason('You have already reviewed this tour. Thank you!');
        setStep('already');
        return;
      }
      setStep('form');
    } catch (err) {
      toast.error('Could not verify eligibility. Please try again.');
    }
  };

  useEffect(() => {
    if (prefillEmail && step === 'checking') {
      checkEligibility(prefillEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) return toast.error('Please select a star rating');
    if (!firstName.trim()) return toast.error('Please enter your first name');
    if (!comment.trim()) return toast.error('Please write a short review');

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/verified`, {
        tour_id: tourId,
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        rating,
        comment: comment.trim(),
      });
      toast.success('Review posted — thank you!');
      setStep('done');
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to post review');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'done' || step === 'already') {
    return (
      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-5 flex items-center gap-3" data-testid="review-done-notice">
        <ShieldCheck className="w-6 h-6 text-emerald-600 flex-shrink-0" />
        <p className="text-emerald-800 font-semibold">
          {step === 'done' ? 'Thanks! Your review is live.' : ineligibleReason}
        </p>
      </div>
    );
  }

  if (step === 'ineligible') {
    return (
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5" data-testid="review-ineligible-notice">
        <p className="text-amber-800 font-semibold mb-2">
          {ineligibleReason}
        </p>
        <p className="text-amber-700 text-sm">
          Only travelers who have started this tour can leave a verified review.
        </p>
        <button
          onClick={() => { setStep('email'); setEmail(''); }}
          className="mt-3 text-sm underline text-amber-800"
        >
          Try a different email
        </button>
      </div>
    );
  }

  if (step === 'email' || step === 'checking') {
    return (
      <form
        onSubmit={(e) => { e.preventDefault(); checkEligibility(email); }}
        className="bg-white border-2 border-cyan-200 rounded-xl p-5"
        data-testid="verified-review-email-form"
      >
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-cyan-600" />
          <h4 className="font-bold text-gray-900">Leave a Verified Review</h4>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Enter the email you used to start this tour. We'll confirm you're a verified listener.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="verified-review-email-input"
            className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
            required
          />
          <button
            type="submit"
            data-testid="verified-review-continue-btn"
            className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-bold transition-all"
          >
            Continue
          </button>
        </div>
      </form>
    );
  }

  // step === 'form'
  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border-2 border-cyan-200 rounded-xl p-6"
      data-testid="verified-review-form"
    >
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5 text-emerald-600" />
        <h4 className="font-bold text-gray-900">You're a verified listener — tell others about it</h4>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Your Rating</label>
        <StarRating rating={rating} size="lg" interactive onRate={setRating} />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">First name</label>
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="e.g. Alex"
          maxLength={60}
          data-testid="verified-review-firstname-input"
          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Short review</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="A line or two about your experience…"
          rows={3}
          maxLength={2000}
          data-testid="verified-review-comment-input"
          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">{comment.length}/2000 characters</p>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="verified-review-submit-btn"
        className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300 text-white py-3 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
      >
        <Send className="w-4 h-4" />
        {submitting ? 'Posting…' : 'Post Verified Review'}
      </button>
    </form>
  );
};

export default VerifiedReviewForm;
